import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SyncLoader from "react-spinners/SyncLoader";
import * as Dialog from "@radix-ui/react-dialog";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListImageCategoryApiProps from "./dtos/IListImageCategoryApiProps";
import IListImageCategoryDataParams from "./dtos/IListImageCategoryDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";

import penIcon from "../../../../../../shared/assets/pen-icon.svg";
import trashIcon from "../../../../../../shared/assets/trash-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerCreate,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
} from "./styles";

const ListImageCategory: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataImageCategoryList, setDataImageCategoryList] =
    useState<IListImageCategoryApiProps>({
      pagination: 0,
      dataArray: [],
    });
  const [listImageCategoryDataParams, setListImageCategoryDataParams] =
    useState<IListImageCategoryDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
    });

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_image_categories;
  const userPermissionRead =
    dataUseAuth.admin_user_permissions.read_image_categories;
  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_image_categories;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_image_categories;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/image-category");
  }, []);

  const handleCreateImageCategory = useCallback(() => {
    navigate("/create-image-category", {
      state: { params: listImageCategoryDataParams },
    });
  }, [navigate, listImageCategoryDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/bits-image-categories", {
        params: listImageCategoryDataParams,
      });

      setDataImageCategoryList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listImageCategoryDataParams]);

  const handleDeleteImageCategory = useCallback(
    async (id: string) => {
      await api.delete(`/bits-image-categories/${id}`);
      setReload(!reload);
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);
        if (formData === "") {
          setListImageCategoryDataParams({
            pagination: 0,
          });
        } else {
          setListImageCategoryDataParams({
            pagination: 0,
            search: formData,
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-image-category/${item.id}`, {
        state: { params: listImageCategoryDataParams },
      });
    },
    [navigate, listImageCategoryDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Listar categorias de ilustrações" />
      <MasterMain>
        <Main>
          <ContainerCreate>
            <Button
              onClick={handleCreateImageCategory}
              disabled={!userPermissionCreate}
            >
              Criar categoria de ilustrações
            </Button>
          </ContainerCreate>

          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Categorias de ilustrações</TableTh>
                    <TableTh>Data de criação</TableTh>
                    <TableTh>Última modificação</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataImageCategoryList.dataArray.map((item, index) => (
                    <TableTr key={item.id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.title}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.updated_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Tooltip title="Editar categoria" placement="top">
                            <ButtonFlexBox
                              onClick={() => {
                                navigate(`/edit-image-category/${item.id}`, {
                                  state: {
                                    params: listImageCategoryDataParams,
                                  },
                                });
                              }}
                              disabled={!userPermissionUpdate}
                            >
                              <IconSvg width="18" height="18" image={penIcon} />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Dialog.Root>
                            <Tooltip title="Deletar categoria" placement="top">
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionDelete}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={trashIcon}
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title="Deletar categoria de ilustrações"
                              subtitle={item.title}
                              type="delete"
                              callback={() =>
                                handleDeleteImageCategory(item.id)
                              }
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataImageCategoryList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listImageCategoryDataParams.pagination}
                setDataParams={setListImageCategoryDataParams}
                dataParams={listImageCategoryDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListImageCategory;
